import React from "react";
import { useLanguage } from "@/context/LanguageContext";
import { Boxes, Workflow, Users, LineChart, ArrowUpRight, ShieldCheck, Zap } from "lucide-react";

// Per-platform pricing + external pricing pages.
// External URLs are intentionally pointing to each LOB platform pricing route.
// Update these when the dedicated platforms go live.
const LOB_PLATFORMS = [
  {
    key: "lob_model",
    name: "BICENTER AI MODEL",
    domain: "https://model.bicenter-ai.com",
    pricingPath: "/pricing",
    Icon: Boxes,
    accent: "from-bicenter-cyan/30 to-bicenter-blue/0",
    border: "border-bicenter-cyan/40",
    plans: {
      fr: [
        { name: "Starter", price: "49€", per: "/ mois", features: ["1 business model IA / mois", "Génération d'idées illimitée", "Export PDF"] },
        { name: "Growth", price: "149€", per: "/ mois", features: ["10 business models / mois", "Prévisions financières", "Modèle pitch deck", "Support email"], popular: true },
        { name: "Scale", price: "Sur devis", per: "", features: ["Illimité", "Multi-utilisateurs", "API & intégrations", "CSM dédié"] },
      ],
      en: [
        { name: "Starter", price: "$49", per: "/ month", features: ["1 AI business model / month", "Unlimited idea generation", "PDF export"] },
        { name: "Growth", price: "$149", per: "/ month", features: ["10 business models / month", "Financial forecasts", "Pitch deck templates", "Email support"], popular: true },
        { name: "Scale", price: "On request", per: "", features: ["Unlimited", "Multi-user", "API & integrations", "Dedicated CSM"] },
      ],
    },
  },
  {
    key: "lob_automation",
    name: "BICENTER AI AUTOMATION",
    domain: "https://automation.bicenter-ai.com",
    pricingPath: "/pricing",
    Icon: Workflow,
    accent: "from-bicenter-violet/30 to-bicenter-magenta/0",
    border: "border-bicenter-violet/40",
    plans: {
      fr: [
        { name: "Starter", price: "79€", per: "/ mois", features: ["100 automatisations / mois", "Templates contrats", "Workflows email"] },
        { name: "Pro", price: "249€", per: "/ mois", features: ["1 000 automatisations", "Contrats juridiques IA", "Intégrations Zapier/n8n", "Support prioritaire"], popular: true },
        { name: "Enterprise", price: "Sur devis", per: "", features: ["Volume illimité", "SLA 99.9%", "Déploiement on-prem", "CSM dédié"] },
      ],
      en: [
        { name: "Starter", price: "$79", per: "/ month", features: ["100 automations / month", "Contract templates", "Email workflows"] },
        { name: "Pro", price: "$249", per: "/ month", features: ["1,000 automations", "AI legal contracts", "Zapier/n8n integrations", "Priority support"], popular: true },
        { name: "Enterprise", price: "On request", per: "", features: ["Unlimited volume", "99.9% SLA", "On-prem deployment", "Dedicated CSM"] },
      ],
    },
  },
  {
    key: "lob_decision",
    name: "BICENTER AI DÉCISION",
    domain: "https://decision.bicenter-ai.com",
    pricingPath: "/pricing",
    Icon: LineChart,
    accent: "from-bicenter-magenta/30 to-bicenter-violet/0",
    border: "border-bicenter-magenta/40",
    plans: {
      fr: [
        { name: "Discovery", price: "99€", per: "/ mois", features: ["Diagnostic stratégique IA", "1 recommandation / mois", "Reporting exécutif"] },
        { name: "Advisory", price: "299€", per: "/ mois", features: ["Recommandations illimitées", "Coaching stratégique IA", "Ajustements < 5 min", "Tableaux de bord décideurs"], popular: true },
        { name: "C-Level", price: "Sur devis", per: "", features: ["Conseil multi-BU", "Modèles IA personnalisés", "Sparring stratégique", "Accès direct CEO BICENTER AI"] },
      ],
      en: [
        { name: "Discovery", price: "$99", per: "/ month", features: ["AI strategic diagnosis", "1 recommendation / month", "Executive reporting"] },
        { name: "Advisory", price: "$299", per: "/ month", features: ["Unlimited recommendations", "AI strategic coaching", "Adjustments in < 5 min", "Decision-maker dashboards"], popular: true },
        { name: "C-Level", price: "On request", per: "", features: ["Multi-BU advisory", "Custom AI models", "Strategic sparring", "Direct line to BICENTER AI CEO"] },
      ],
    },
  },
  {
    key: "lob_rh",
    name: "BICENTER AI RH",
    domain: "https://rh.bicenter-ai.com",
    pricingPath: "/pricing",
    Icon: Users,
    accent: "from-bicenter-blue/30 to-bicenter-violet/0",
    border: "border-bicenter-blue/40",
    plans: {
      fr: [
        { name: "Starter", price: "59€", per: "/ mois / utilisateur", features: ["10 mises en relation / mois", "Marketplace candidats-professionnels", "Descriptions de poste IA (indicatives)"] },
        { name: "Team", price: "129€", per: "/ mois / utilisateur", features: ["Mises en relation illimitées", "Suggestions transparentes IA", "Pilotage RH temps réel sous supervision humaine", "Intégrations ATS"], popular: true },
        { name: "Enterprise", price: "Sur devis", per: "", features: ["Multi-équipes", "Conformité RGPD avancée", "SSO", "Onboarding dédié"] },
      ],
      en: [
        { name: "Starter", price: "$59", per: "/ month / seat", features: ["10 matchings / month", "Candidate-professional marketplace", "AI job descriptions (indicative)"] },
        { name: "Team", price: "$129", per: "/ month / seat", features: ["Unlimited matchings", "Transparent AI suggestions", "Real-time HR piloting under human oversight", "ATS integrations"], popular: true },
        { name: "Enterprise", price: "On request", per: "", features: ["Multi-team", "Advanced GDPR", "SSO", "Dedicated onboarding"] },
      ],
    },
  },
];

export const LOBActivate = () => {
  const { t, lang } = useLanguage();
  const labels = t.activate;

  return (
    <section id="activate" className="relative py-28 md:py-36" data-testid="activate-section">
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-0 left-1/4 w-[480px] h-[480px] rounded-full bg-bicenter-cyan/10 blur-[180px]"></div>
        <div className="absolute bottom-0 right-1/4 w-[420px] h-[420px] rounded-full bg-bicenter-magenta/10 blur-[160px]"></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <p className="text-xs uppercase tracking-[0.4em] text-bicenter-magenta font-mono">{labels.kicker}</p>
          <h2 className="mt-4 font-display text-4xl md:text-5xl lg:text-6xl font-light tracking-tight text-white leading-[1.05]">
            {labels.title}
          </h2>
          <p className="mt-6 text-lg text-zinc-400 font-body leading-relaxed">{labels.subtitle}</p>

          <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.03] border border-white/10 text-xs text-zinc-300">
            <ShieldCheck size={12} className="text-bicenter-cyan" />
            <span>{labels.secure_payment}</span>
          </div>
        </div>

        <div className="space-y-16">
          {LOB_PLATFORMS.map((p) => {
            const Icon = p.Icon;
            const plans = p.plans[lang] || p.plans.fr;
            const pricingUrl = `${p.domain}${p.pricingPath}`;
            return (
              <div
                key={p.key}
                data-testid={`activate-platform-${p.key}`}
                className="border border-white/10 rounded-3xl p-6 md:p-10 bg-[#0c0c0e]"
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
                  <div className="flex items-center gap-4">
                    <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${p.accent} border ${p.border} flex items-center justify-center`}>
                      <Icon size={22} className="text-white" />
                    </div>
                    <div>
                      <h3 className="font-display text-2xl md:text-3xl text-white font-medium tracking-tight">
                        {p.name}
                      </h3>
                      <p className="text-xs text-zinc-500 mt-1 font-mono">{p.domain.replace("https://", "")}</p>
                    </div>
                  </div>

                  <a
                    href={pricingUrl}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`activate-cta-${p.key}`}
                    className={`inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-medium border ${p.border} bg-white/[0.02] text-white hover:bg-white/[0.06] transition-all`}
                  >
                    <Zap size={14} />
                    {labels.cta_pricing}
                    <ArrowUpRight size={14} />
                  </a>
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                  {plans.map((plan, i) => (
                    <div
                      key={plan.name}
                      data-testid={`activate-plan-${p.key}-${i}`}
                      className={`relative rounded-2xl p-6 ${
                        plan.popular
                          ? "bg-gradient-to-br from-bicenter-cyan/10 to-bicenter-violet/10 border border-bicenter-cyan/30"
                          : "bg-[#101013] border border-white/5"
                      }`}
                    >
                      {plan.popular && (
                        <span className="absolute -top-3 left-6 text-[10px] uppercase tracking-[0.3em] bg-gradient-to-r from-bicenter-cyan to-bicenter-blue text-white px-3 py-1 rounded-full">
                          {labels.popular}
                        </span>
                      )}

                      <p className="text-xs uppercase tracking-[0.25em] text-zinc-500">{plan.name}</p>
                      <div className="mt-3 flex items-baseline gap-1">
                        <span className="font-display text-3xl text-white font-medium">{plan.price}</span>
                        <span className="text-sm text-zinc-500">{plan.per}</span>
                      </div>

                      <ul className="mt-5 space-y-2">
                        {plan.features.map((f, j) => (
                          <li key={j} className="flex items-start gap-2 text-sm text-zinc-300">
                            <span className="mt-1.5 w-1 h-1 rounded-full bg-bicenter-cyan flex-shrink-0"></span>
                            <span>{f}</span>
                          </li>
                        ))}
                      </ul>

                      <a
                        href={`${pricingUrl}?plan=${plan.name.toLowerCase()}`}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`activate-plan-cta-${p.key}-${i}`}
                        className={`mt-6 inline-flex items-center justify-between gap-2 w-full text-sm font-medium rounded-full py-2.5 px-4 transition-all ${
                          plan.popular
                            ? "bg-white text-black hover:bg-zinc-200"
                            : "border border-white/10 text-white hover:bg-white/5"
                        }`}
                      >
                        {labels.cta_activate}
                        <ArrowUpRight size={14} />
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <p className="mt-12 text-center text-xs text-zinc-500 max-w-2xl mx-auto">
          {labels.disclaimer}
        </p>
      </div>
    </section>
  );
};
